'use client';

import { type RefObject, useEffect, useState } from 'react';

import { getBookingScrollContainer } from '../booking-scroll-container';

/**
 * Bottom edge of the app's fixed or sticky header (the native top bar, the
 * website header) inside the list's scroll area, so the sticky strip sits
 * just below it. Re-measured when a header changes size (logo or venue bar
 * loading in after mount would otherwise leave a see-through gap).
 */
export function useFixedHeaderBottom(
  rootRef: RefObject<HTMLElement | null>,
  remeasureKey: unknown,
) {
  const [bottom, setBottom] = useState(0);

  useEffect(() => {
    const headers = Array.from(document.querySelectorAll('header')).filter(
      (header) => {
        const { position } = getComputedStyle(header);
        return position === 'fixed' || position === 'sticky';
      },
    );
    const measure = () => {
      const root = rootRef.current;
      const containerTop = root
        ? (getBookingScrollContainer(root)?.getBoundingClientRect().top ?? 0)
        : 0;
      let next = 0;
      headers.forEach((header) => {
        const rect = header.getBoundingClientRect();
        if (rect.top <= containerTop + 1) {
          next = Math.max(next, rect.bottom - containerTop);
        }
      });
      setBottom(Math.max(0, Math.floor(next)));
    };
    const observer = new ResizeObserver(measure);
    headers.forEach((header) => observer.observe(header));
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [rootRef, remeasureKey]);

  return bottom;
}
